/**
 * RPC Provider Context
 * Provides RPC provider state and management to React components
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from 'react';
import { RPCProviderManager } from '@/services/rpc/RPCProviderManager';
import { RPCProviderState, ProviderId, RPCProviderConfig } from '@/services/rpc/types';
import { getSolanaEndpoint, getSolanaWsEndpoint, getTritonRpcUrl, getTritonWsUrl, getAlchemyRpcUrl } from '@/lib/solana/config';

interface RPCProviderContextValue {
  state: RPCProviderState | null;
  manager: RPCProviderManager | null;
  httpEndpoint: string;
  wsEndpoint: string | undefined;
  setUserPreference: (preference: 'auto' | ProviderId) => void;
  switchProvider: (providerId: ProviderId) => boolean;
  isInitialized: boolean;
}

const RPCProviderContext = createContext<RPCProviderContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function RPCProviderProvider({ children }: ProviderProps) {
  const [manager, setManager] = useState<RPCProviderManager | null>(null);
  const [state, setState] = useState<RPCProviderState | null>(null);

  // Build config from environment/runtime sources.
  // The manager registers Triton primary + Alchemy fallback. The proxy URL
  // via getSolanaEndpoint() is consumed directly by 21 frontend bypass call
  // sites and the local rpcEndpoint fallback below.
  const config: RPCProviderConfig = useMemo(() => ({
    tritonRpcUrl: getTritonRpcUrl(),
    tritonWsUrl: getTritonWsUrl(),
    alchemyRpcUrl: getAlchemyRpcUrl(),
  }), []);

  const rpcEndpoint = useMemo(() => getSolanaEndpoint(), []);
  const baseWsEndpoint = useMemo(() => getSolanaWsEndpoint(), []);

  // Initialize manager
  useEffect(() => {
    if (!config.tritonRpcUrl && !config.alchemyRpcUrl) {
      console.warn('[RPCProviderContext] No RPC URLs configured');
      return;
    }

    const newManager = new RPCProviderManager(config);
    setManager(newManager);
    setState(newManager.getState());

    // Subscribe to state changes
    const unsubscribe = newManager.onStateChange((newState) => {
      setState(newState);
    });

    // Start health monitoring
    newManager.startHealthMonitoring();

    return () => {
      unsubscribe();
      newManager.destroy();
    };
  }, [config.tritonRpcUrl, config.tritonWsUrl, config.alchemyRpcUrl]);

  const httpEndpoint = useMemo(() => {
    return manager?.getHttpEndpoint() || config.tritonRpcUrl || rpcEndpoint || '';
  }, [manager, state?.activeProvider, config.tritonRpcUrl, rpcEndpoint]);

  const wsEndpoint = useMemo(() => {
    return manager?.getWsEndpoint() || config.tritonWsUrl || baseWsEndpoint;
  }, [manager, state?.activeProvider, config.tritonWsUrl, baseWsEndpoint]);

  const setUserPreference = useCallback((preference: 'auto' | ProviderId) => {
    manager?.setUserPreference(preference);
  }, [manager]);

  const switchProvider = useCallback((providerId: ProviderId): boolean => {
    return manager?.switchProvider(providerId) || false;
  }, [manager]);

  const value: RPCProviderContextValue = {
    state,
    manager,
    httpEndpoint,
    wsEndpoint,
    setUserPreference,
    switchProvider,
    isInitialized: manager !== null,
  };

  return (
    <RPCProviderContext.Provider value={value}>
      {children}
    </RPCProviderContext.Provider>
  );
}

export function useRPCProviderContext(): RPCProviderContextValue {
  const context = useContext(RPCProviderContext);
  if (!context) {
    throw new Error('useRPCProviderContext must be used within RPCProviderProvider');
  }
  return context;
}

export { RPCProviderContext };
